import { v4 as uuidv4 } from 'uuid';

import { SelectOption } from 'types';
import { AuthUser } from 'types/interfaces/user/AuthUser';

import { extractPathnameFromHref } from 'helpers/extractPathnameFromHref';

import { httpClient } from './httpClient';
import { TrackingApi } from './TrackingApi';

export interface InitResponse {
  user: AuthUser;
  dialogs: { count_new: number };
  has_notifications: boolean;
  site_language: {
    options: string[];
    value: string;
  };
  translation: {
    enabled: boolean;
    build_id: number;
  };
  popular_users: PopularUser[];
  vip_boost_expired_at: string | null;
  can_see_ads: boolean;
  can_see_native_ads: boolean;
  show_retention_popup: boolean;
  show_gift_credits_popup: boolean;
  need_age_verification: boolean;
  is_custom_vip_funnel_enabled: boolean;
  is_intent_form_enabled: boolean;
  is_compliance_statement_enabled: boolean;
  is_static_popular_users_photo_enabled: boolean;
  is_carousel_paywall_v4_enabled: boolean;
  is_retention_activity: boolean;
  is_free_vip_enabled: boolean;
  is_dates_me_paywall: boolean;
  is_organic_partner_id: boolean;
  is_month_package: boolean;
}

export type LandingInitResponse = {
  csrf_token: string | null;
  landing_validation_split: boolean;
  is_additional_price: boolean;
};

export type RegisterPayload = {
  email: string;
  password: string;
  gender: string;
  age: number;
  city?: number;
  city_id?: number;
  preland: {
    id: number;
    screen_number: number;
    history_length: number | null;
    params?: Record<
      string,
      string | number | boolean | SelectOption[] | undefined
    >;
  };
};

export interface AutoRegisterPayload {
  [key: string]: string | (string | null)[] | null | undefined;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export interface LoginHeaders {
  csrfToken: string | null;
}

interface GoogleResponse {
  iss: string;
  azp: string;
  aud: string;
  sub: string;
  email: string;
  email_verified: boolean;
  nbf: number;
  name: string;
  picture: string;
  given_name: string;
  family_name: string;
  locale: string;
  iat: number;
  exp: number;
  jti: string;
}

export enum PackagesThemeGroups {
  Default = 'default',
  FreeTrial = 'trial-group-2',
}

export type PopularUser = {
  id: number;
  photo_url: string;
};

export const AuthApi = {
  async init({
    from,
  }: {
    from: string;
    userId?: string;
  }): Promise<InitResponse> {
    const initLoadStart = Date.now();
    const initId = uuidv4();

    const { data } = await httpClient.get<InitResponse>('/init', {
      params: {
        from: extractPathnameFromHref(from) || 'ref',
        ft: initLoadStart,
      },
    });

    TrackingApi.trackInitLoadEnd({ initLoadStart, initId });

    return data;
  },

  async landingInit(user_id?: string) {
    const { data } = await httpClient.get<LandingInitResponse>(
      '/landing/init',
      {
        params: { user_id },
      }
    );

    return data;
  },

  async register(payload: RegisterPayload) {
    const { data } = await httpClient.post<AuthUser>('/register', payload);

    return data;
  },

  async autoRegister(payload: AutoRegisterPayload) {
    const { data } = await httpClient.get<AuthUser>('/register/auto', {
      params: payload,
    });

    return data;
  },

  async login(payload: LoginPayload, headers: LoginHeaders) {
    const { data } = await httpClient.post<AuthUser>('/login', payload, {
      headers: {
        ...(headers.csrfToken && { 'X-CSRF-TOKEN': headers.csrfToken }),
      },
    });

    return data;
  },

  async logout() {
    return httpClient.get('/logout');
  },

  async checkEmail({ email }: { email: string }) {
    await httpClient.post('/register/check-email', { email, unique: email });
  },

  async checkCode({ code }: { code: string }) {
    await httpClient.get(`/recovery/check-code/${code}`);
  },

  async sendVerificationCode({ email }: { email: string }) {
    await httpClient.post('/recovery/send-mail', { email });
  },

  async changePassword({ password, code }: { password: string; code: string }) {
    await httpClient.post(`/recovery/change-password/${code}`, { password });
  },

  async fetchEmailFromGoogle(jwtToken: string) {
    const { data } = await httpClient.post<GoogleResponse>('/auth/google', {
      jwt: jwtToken,
    });

    return data;
  },
};
